import { apiService } from './apiService';

export interface SobrietyMilestone {
  id: string;
  user_id: string;
  milestone_days: number;
  milestone_label: string;
  achieved_at: string;
  celebration_sent: boolean;
  created_at: string;
}

export interface MilestoneDefinition {
  days: number;
  label: string;
  celebrationMessage: string;
  importance: 'high' | 'medium' | 'low';
}

class SobrietyMilestoneService {
  private static instance: SobrietyMilestoneService;

  static getInstance(): SobrietyMilestoneService {
    if (!SobrietyMilestoneService.instance) {
      SobrietyMilestoneService.instance = new SobrietyMilestoneService();
    }
    return SobrietyMilestoneService.instance;
  }

  // Comprehensive milestone definitions (static, used client-side for celebrations)
  private milestoneDefinitions: MilestoneDefinition[] = [
    // Early days - most critical
    { days: 1, label: '24 horas', celebrationMessage: 'Primeira vitória conquistada! 🎉', importance: 'high' },
    { days: 3, label: '3 dias', celebrationMessage: 'Três dias de força e determinação! 💪', importance: 'high' },
    { days: 7, label: '1 semana', celebrationMessage: 'Uma semana completa! Que conquista! 🏆', importance: 'high' },
    { days: 10, label: '10 dias', celebrationMessage: '10 dias de sobriedade! Parabéns! 👏', importance: 'medium' },
    { days: 14, label: '2 semanas', celebrationMessage: 'Duas semanas sólidas! Você está no caminho certo! ⭐', importance: 'high' },
    
    // Monthly milestones
    { days: 20, label: '20 dias', celebrationMessage: '20 dias! Sua determinação é inspiradora! 🌟', importance: 'medium' },
    { days: 30, label: '1 mês', celebrationMessage: 'UM MÊS COMPLETO! Marco histórico! 🎊', importance: 'high' },
    { days: 45, label: '45 dias', celebrationMessage: '45 dias! Você está construindo uma nova vida! 🏗️', importance: 'medium' },
    { days: 60, label: '2 meses', celebrationMessage: 'DOIS MESES! Transformação real acontecendo! 🦋', importance: 'high' },
    { days: 75, label: '75 dias', celebrationMessage: '75 dias! Sua perseverança é admirável! 💎', importance: 'medium' },
    { days: 90, label: '3 meses', celebrationMessage: 'TRÊS MESES! Trimestre de vitórias! 🏅', importance: 'high' },
    
    // Longer milestones
    { days: 120, label: '4 meses', celebrationMessage: 'Quatro meses de sobriedade! Que jornada! 🚀', importance: 'high' },
    { days: 150, label: '5 meses', celebrationMessage: 'Cinco meses! Você é um exemplo de superação! 🌈', importance: 'medium' },
    { days: 180, label: '6 meses', celebrationMessage: 'MEIO ANO! Transformação completa! 🎯', importance: 'high' },
    { days: 210, label: '7 meses', celebrationMessage: 'Sete meses! Sua nova vida está consolidada! 🏛️', importance: 'medium' },
    { days: 240, label: '8 meses', celebrationMessage: 'Oito meses! Você é prova de que é possível! 💫', importance: 'medium' },
    { days: 270, label: '9 meses', celebrationMessage: 'Nove meses! Quase um ano de conquistas! 📈', importance: 'medium' },
    { days: 300, label: '10 meses', celebrationMessage: 'Dez meses! A reta final para um ano! 🏃‍♂️', importance: 'medium' },
    { days: 330, label: '11 meses', celebrationMessage: 'Onze meses! Um ano está quase aí! ⏰', importance: 'medium' },
    
    // Annual milestones
    { days: 365, label: '1 ano', celebrationMessage: 'UM ANO COMPLETO! VOCÊ CONSEGUIU! 🏆🎉🎊', importance: 'high' },
    { days: 548, label: '18 meses', celebrationMessage: 'Um ano e meio! Sua jornada é inspiradora! 🌟', importance: 'high' },
    { days: 730, label: '2 anos', celebrationMessage: 'DOIS ANOS! Você é um verdadeiro guerreiro! ⚔️', importance: 'high' },
    { days: 1095, label: '3 anos', celebrationMessage: 'TRÊS ANOS! Sua transformação é extraordinária! 👑', importance: 'high' },
    { days: 1460, label: '4 anos', celebrationMessage: 'QUATRO ANOS! Você mudou sua vida completamente! 🌍', importance: 'high' },
    { days: 1825, label: '5 anos', celebrationMessage: 'CINCO ANOS! Você é uma lenda da recuperação! 🏛️', importance: 'high' },
  ];

  /**
   * Check and record a milestone via API (bypasses RLS timing issues)
   * Note: userId parameter is kept for backwards compatibility but not used (API uses JWT)
   */
  async checkAndRecordMilestone(_userId: string, currentDays: number, previousDays: number = 0): Promise<MilestoneDefinition | null> {
    try {
      // Check if sobriety congratulations are enabled via API
      const configEnabled = await this.isSobrietyCongratsEnabled();

      if (!configEnabled) {
        console.log('🔕 SobrietyMilestoneService: Sobriety congratulations disabled in config');
        return null;
      }

      // Find the next milestone that should be achieved based on current days
      // Only consider milestones that are exactly at or just reached with current days
      const achievedMilestone = this.milestoneDefinitions.find(milestone => 
        milestone.days === currentDays || 
        (currentDays >= milestone.days && previousDays < milestone.days)
      );

      if (!achievedMilestone) {
        return null;
      }

      // Special logic for 24 hours (1 day) milestone
      // Only celebrate if user exactly hit 1 day, not if they're already past it
      if (achievedMilestone.days === 1 && currentDays > 1) {
        console.log('🕐 SobrietyMilestoneService: User already past 24h milestone, skipping daily celebration');
        return null;
      }

      console.log('🎉 SobrietyMilestoneService: Milestone achieved!', {
        milestone: achievedMilestone,
        currentDays,
        previousDays
      });

      // Record milestone via API (uses upsert, handles duplicates)
      try {
        const response = await apiService.post<{
          success: boolean;
          milestone?: SobrietyMilestone;
          error?: string;
        }>('/api/sobriety/milestones', {
          milestone_days: achievedMilestone.days,
          milestone_label: achievedMilestone.label
        });

        if (response.success && response.data?.success) {
          console.log('✅ SobrietyMilestoneService: Milestone recorded via API:', response.data.milestone);
        } else {
          console.warn('⚠️ SobrietyMilestoneService: Milestone recording returned error:', response.data?.error || response.error);
        }
      } catch (recordError) {
        console.error('Error recording milestone via API:', recordError);
        // Don't fail the celebration if DB save fails
      }

      return achievedMilestone;

    } catch (error) {
      console.error('Error in checkAndRecordMilestone:', error);
      // Return milestone anyway so celebration can still happen
      return this.milestoneDefinitions.find(milestone => 
        currentDays >= milestone.days && previousDays < milestone.days
      ) || null;
    }
  }

  /**
   * Get user's milestones via API (bypasses RLS timing issues)
   * Note: userId parameter is kept for backwards compatibility but not used (API uses JWT)
   */
  async getUserMilestones(_userId: string): Promise<SobrietyMilestone[]> {
    try {
      console.log('🏆 SobrietyMilestoneService: Loading milestones via API...');

      const response = await apiService.get<{
        success: boolean;
        milestones?: SobrietyMilestone[];
        error?: string;
      }>('/api/sobriety/milestones');

      if (!response.success || !response.data?.success) {
        throw new Error(response.data?.error || response.error || 'Failed to fetch milestones');
      }

      console.log('✅ SobrietyMilestoneService: Milestones loaded via API:', response.data.milestones?.length || 0);
      return response.data.milestones || [];

    } catch (error) {
      console.error('Error in getUserMilestones:', error);
      return [];
    }
  }

  async getNextMilestone(currentDays: number): Promise<MilestoneDefinition | null> {
    // Pure client-side calculation - no API needed
    const nextMilestone = this.milestoneDefinitions.find(milestone => 
      milestone.days > currentDays
    );

    return nextMilestone || null;
  }

  /**
   * Check if sobriety congratulations are enabled via API (bypasses RLS timing issues)
   */
  async isSobrietyCongratsEnabled(): Promise<boolean> {
    try {
      console.log('⚙️ SobrietyMilestoneService: Checking config via API...');

      const response = await apiService.get<{
        success: boolean;
        sobrietyCongratsEnabled?: boolean;
        error?: string;
      }>('/api/sobriety/config');

      if (!response.success || !response.data?.success) {
        console.warn('Failed to check sobriety congrats config via API, defaulting to enabled');
        return true;
      }

      console.log('✅ SobrietyMilestoneService: Config loaded via API:', response.data.sobrietyCongratsEnabled);
      return response.data.sobrietyCongratsEnabled !== false;

    } catch (error) {
      console.warn('Failed to check sobriety congrats config, defaulting to enabled:', error);
      return true;
    }
  }

  getMilestoneDefinitions(): MilestoneDefinition[] {
    return [...this.milestoneDefinitions];
  }

  generateCelebrationPrompt(milestone: MilestoneDefinition, _currentDays: number): string {
    const basePrompts = {
      high: `🎉 MARCO HISTÓRICO ALCANÇADO! 🎉|||O usuário acabou de completar ${milestone.label} de sobriedade!|||${milestone.celebrationMessage}|||Esta é uma conquista EXTRAORDINÁRIA que merece toda celebração!|||Cada dia até aqui foi uma vitória, e ${milestone.label} é um marco que poucos conseguem!|||Você está reescrevendo sua história e provando sua força!|||Continue assim, o próximo marco está esperando por você! 💪`,
      
      medium: `🎊 Parabéns pela conquista! 🎊|||${milestone.label} de sobriedade completados!|||${milestone.celebrationMessage}|||Sua determinação está construindo algo incrível!|||Cada dia é uma prova da sua força interior!|||Vamos em frente, você está no caminho certo! ⭐`,
      
      low: `👏 Que orgulho de você! 👏|||${milestone.label} é mais um passo importante!|||${milestone.celebrationMessage}|||Sua jornada está sendo linda de acompanhar!|||Continue firme, cada dia conta! 💚`
    };

    return basePrompts[milestone.importance];
  }
}

export const sobrietyMilestoneService = SobrietyMilestoneService.getInstance();
